<template>
  <div class="content-dashboard">
    <div v-if="isInitialLoading" class="text-center py-5">
      <div class="spinner-border text-primary" role="status" style="width: 3rem; height: 3rem">
        <span class="visually-hidden">Chargement...</span>
      </div>
      <p class="mt-3 text-muted">Vérification de votre abonnement...</p>
    </div>

    <div v-else-if="mode === 'params-home'">
      <h3>{{ $t("param_calc.clinic_info") }}</h3>

      <div class="row justify-content-center align-items-center mt-4 mt-5">
        <div class="col-lg-7">
          <div class="form-input flex-row align-items-center">
            <label for="pwd">{{ $t("param_calc.supplyvet_code") }}</label>
            <input
              type="text"
              class="form-control w-50"
              id="pwd"
              :value="veterinaireDivaltoId"
              :placeholder="$t('param_calc.code_placeholder')"
              disabled
              required
            />
          </div>

          <div v-if="subscriptionMessage" class="alert alert-warning mt-4">
            <p class="mb-3">{{ subscriptionMessage }}</p>
            <router-link to="/contact" class="btn btn-outline-orange"> Contactez-nous </router-link>
          </div>

          <div v-if="errorMessage" class="alert alert-danger mt-4">
            {{ errorMessage }}
          </div>
        </div>
      </div>
    </div>

    <div v-else-if="mode === 'tarifs-config'">
      <div class="d-flex gap-2 align-items-center mb-3">
        <h3>Tarifs des produits exclus de votre catalogue tarifaire</h3>
      </div>

      <div class="tarifs-filters mt-3 mb-4">
        <div class="check-block d-flex gap-3 align-items-center">
          <label
            v-for="option in options"
            :key="option"
            :class="['check-button', { active: selected === option.value }]"
          >
            <input
              type="radio"
              :value="option.value"
              v-model="selected"
              hidden
              @change="handleOptionChange(option.value)"
            />
            <span class="label-text">{{ option.name }}</span>
            <span class="check-icon" v-if="selected === option.value"><i class="ti ti-check-box ps-2"></i></span>
          </label>
          <button type="button" class="check-button ms-auto" @click="showResetConfirmation" :disabled="isResetting">
            <span class="label-text">
              <span v-if="isResetting" class="spinner-border spinner-border-sm me-2" role="status"></span>
              Réinitialiser les prix par défaut
            </span>
          </button>
        </div>
      </div>

      <div v-if="subscriptionMessage" class="alert alert-warning mt-4">
        <p class="mb-3">{{ subscriptionMessage }}</p>
        <router-link to="/contact" class="btn btn-outline-orange"> Contactez-nous </router-link>
      </div>

      <div class="box-chart p-4 d-flex flex-column gap-3 mb-3" v-for="(group, index) in productGroups" :key="index">
        <h6 class="mb-3">{{ group.title }}</h6>
        <div class="row bg-light p-3 mb-3 g-3 align-items-center round-10" v-for="(line, i) in group.lines" :key="i">
          <div class="col-lg-3 col-md-4">
            <strong>{{ line.name }}</strong>
          </div>
          <div class="col-lg-3 col-md-4">
            <div class="input-group input-group-calcul">
              <input
                type="number"
                class="form-control"
                min="0"
                step="0.01"
                v-model.number="line.ht"
                @input="recalculate(line)"
                placeholder="0,00"
              />
              <span class="input-group-text">HT</span>
            </div>
          </div>
          <div class="col-lg-2 col-md-4">
            <div class="input-group input-group-calcul">
              <input
                type="number"
                class="form-control"
                min="0"
                step="1"
                v-model.number="line.tva"
                @input="recalculate(line)"
                placeholder="20"
              />
              <span class="input-group-text">%TVA</span>
            </div>
          </div>
          <div class="col-lg-4">
            <div class="input-group input-group-calcul">
              <input type="number" class="form-control" min="0" step="0.01" :value="formatPrice(line.ttc)" readonly />
              <span class="input-group-text">€TTC</span>
            </div>
          </div>
        </div>
      </div>

      <div v-if="isLoadingProducts" class="text-center py-4">
        <div class="spinner-border text-primary" role="status">
          <span class="visually-hidden">Chargement...</span>
        </div>
        <p class="mt-2">Chargement des produits...</p>
      </div>

      <div v-if="errorMessage && mode === 'tarifs-config'" class="alert alert-danger mt-4">
        {{ errorMessage }}
      </div>

      <div class="d-flex justify-content-center gap-3 mt-4">
        <button type="button" class="btn btn-outline-orange w-15" @click="cancelConfiguration" :disabled="isSaving">
          Annuler
        </button>
        <button type="button" class="btn btn-orange w-15" @click="saveConfigurations" :disabled="isSaving">
          <span v-if="isSaving" class="spinner-border spinner-border-sm me-2" role="status"></span>
          Enregistrer
        </button>
      </div>
    </div>

    <div
      class="modal fade"
      id="resetPricesModal"
      tabindex="-1"
      aria-labelledby="resetPricesModalLabel"
      aria-hidden="true"
    >
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title" id="resetPricesModalLabel">Confirmation de réinitialisation</h5>
            <button type="button" class="btn-close" @click="closeResetModal" aria-label="Fermer"></button>
          </div>
          <div class="modal-body">
            <div class="alert alert-warning">
              <i class="ti ti-alert-triangle me-2"></i>
              <strong>Attention :</strong> Cette action va écraser toute votre configuration actuelle des prix et cette
              action n'est pas réversible.
            </div>
            <p>Êtes-vous sûr de vouloir réinitialiser tous les prix par défaut ?</p>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-outline-orange" @click="closeResetModal">Annuler</button>
            <button type="button" class="btn btn-orange" @click="confirmResetPrices" :disabled="isResetting">
              <span v-if="isResetting" class="spinner-border spinner-border-sm me-2" role="status"></span>
              Confirmer la réinitialisation
            </button>
          </div>
        </div>
      </div>
    </div>

    <div
      class="modal fade"
      id="saveSuccessModal"
      tabindex="-1"
      aria-labelledby="saveSuccessModalLabel"
      aria-hidden="true"
    >
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title text-success" id="saveSuccessModalLabel">
              <i class="ti ti-check-circle me-2"></i>
              Sauvegarde réussie
            </h5>
            <button type="button" class="btn-close" @click="closeSaveSuccessModal" aria-label="Fermer"></button>
          </div>
          <div class="modal-body text-center">
            <div class="mb-3">
              <i class="ti ti-check-circle text-success" style="font-size: 48px"></i>
            </div>
            <p class="mb-0">Les configurations ont été sauvegardées avec succès !</p>
          </div>
          <div class="modal-footer justify-content-center">
            <button type="button" class="btn btn-orange" @click="closeSaveSuccessModal">Fermer</button>
          </div>
        </div>
      </div>
    </div>

    <div
      class="modal fade"
      id="resetSuccessModal"
      tabindex="-1"
      aria-labelledby="resetSuccessModalLabel"
      aria-hidden="true"
    >
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title text-success" id="resetSuccessModalLabel">
              <i class="ti ti-check-circle me-2"></i>
              Réinitialisation réussie
            </h5>
            <button type="button" class="btn-close" @click="closeResetSuccessModal" aria-label="Fermer"></button>
          </div>
          <div class="modal-body text-center">
            <div class="mb-3">
              <i class="ti ti-check-circle text-success" style="font-size: 48px"></i>
            </div>
            <p class="mb-0">Les prix ont été réinitialisés avec succès !</p>
          </div>
          <div class="modal-footer justify-content-center">
            <button type="button" class="btn btn-orange" @click="closeResetSuccessModal">Fermer</button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  import { useRegisterStore } from "@/stores/register";
  import {
    checkSubscriptionStatus,
    getCroquettesProducts,
    updateRationConfigurations,
  } from "@/api/services/parametrageService";

  export default {
    name: "ParametresCalculView",
    data() {
      return {
        options: [],
        selected: "Tous",
        selectedChecks: [],
        mode: "params-home",
        subscriptionMessage: "",
        errorMessage: "",
        isLoadingProducts: false,
        productGroups: [],
        allProductGroups: [],
        originalProductsData: [],
        isSaving: false,
        isResetting: false,
        isInitialLoading: true,
        initialCheckComplete: false,
      };
    },
    computed: {
      veterinaireDivaltoId() {
        const registerStore = useRegisterStore();
        const id = registerStore.Veterinaire?.divalto_id || "";
        return id.replace(/^C/i, "R");
      },
    },
    async mounted() {
      await this.checkInitialSubscriptionStatus();
    },
    methods: {
      async checkInitialSubscriptionStatus() {
        this.isInitialLoading = true;
        this.subscriptionMessage = "";
        this.errorMessage = "";

        try {
          const subscriptionData = await checkSubscriptionStatus();
          if (subscriptionData.active) {
            // Abonnement actif → Afficher première partie avec message
            this.mode = "params-home";
            this.subscriptionMessage =
              "Vous êtes abonnés à la plateforme Supplyvet, vos tarifs sont donc importés depuis votre catalogue. Si vous le souhaitez vous pouvez les modifier directement depuis l'onglet dédié";
          } else {
            // Abonnement inactif → Afficher configurateur avec avertissement
            this.subscriptionMessage = "Votre abonnement n'est pas actif. Vous pouvez configurer vos prix personnalisés ci-dessous.";
            await this.loadConfigurationInterface();
          }
        } catch (error) {
          if (error.response && error.response.status === 404) {
            // Pas d'abonnement → Charger configurateur sans message
            await this.loadConfigurationInterface();
          } else {
            // Erreur → Afficher première partie avec message d'erreur
            this.mode = "params-home";
            this.errorMessage = "Erreur lors de la vérification de l'abonnement. Veuillez réessayer.";
            console.error("Erreur vérification initiale abonnement:", error);
          }
        } finally {
          this.isInitialLoading = false;
          this.initialCheckComplete = true;
        }
      },
      async loadConfigurationInterface() {
        this.isLoadingProducts = true;

        try {
          const productsData = await getCroquettesProducts();
          this.originalProductsData = productsData;
          this.generateFilterOptions(productsData);
          this.allProductGroups = this.transformProductsData(productsData);
          this.applyFilter();
          this.mode = "tarifs-config";
        } catch (error) {
          this.errorMessage = "Erreur lors du chargement des produits. Veuillez réessayer.";
          console.error("Erreur chargement produits:", error);
        } finally {
          this.isLoadingProducts = false;
        }
      },
      generateFilterOptions(apiData) {
        const taxons = new Set();
        const animalTypes = new Set();

        apiData.forEach(product => {
          if (product.taxonName && product.taxonName.trim()) {
            taxons.add(product.taxonName);
          }
          if (product.animalTypeName && product.animalTypeName.trim()) {
            animalTypes.add(product.animalTypeName);
          }
        });
        this.options = [{ name: "Tous", value: "Tous" }];
        Array.from(taxons)
          .sort()
          .forEach(taxon => {
            this.options.push({
              name: taxon,
              value: `taxon:${taxon}`,
              type: "taxon",
            });
          });
        Array.from(animalTypes)
          .sort()
          .forEach(animalType => {
            this.options.push({
              name: animalType,
              value: `animal:${animalType}`,
              type: "animal",
            });
          });
      },

      transformProductsData(apiData) {
        const groups = [];

        apiData.forEach(product => {
          const group = {
            title: product.productName,
            productId: product.productId,
            animalTypeName: product.animalTypeName,
            taxonName: product.taxonName,
            lines: [],
          };
          product.variants.forEach(variant => {
            const priceTtc = variant.configuredPriceTtc || variant.actualPrice;
            const taxRate = variant.taxe || 20;
            const priceHt = priceTtc / (1 + taxRate / 100);
            group.lines.push({
              name: variant.name,
              variantId: variant.variantId,
              ht: Number(priceHt.toFixed(2)),
              tva: taxRate,
              ttc: Number(priceTtc.toFixed(2)),
              originalPrice: variant.actualPrice,
              isConfigured: variant.configuredPriceTtc !== null,
            });
          });

          groups.push(group);
        });

        return groups;
      },
      applyFilter() {
        if (this.selected === "Tous") {
          this.productGroups = [...this.allProductGroups];
        } else if (this.selected.startsWith("taxon:")) {
          const taxonName = this.selected.replace("taxon:", "");
          this.productGroups = this.allProductGroups.filter(group => group.taxonName === taxonName);
        } else if (this.selected.startsWith("animal:")) {
          const animalTypeName = this.selected.replace("animal:", "");
          this.productGroups = this.allProductGroups.filter(group => group.animalTypeName === animalTypeName);
        }

        console.log(`Filtre appliqué: ${this.selected}, ${this.productGroups.length} groupes affichés`);
      },

      recalculate(line) {
        const ht = Number(line.ht) || 0;
        const tva = Number(line.tva) || 0;
        line.ttc = ht * (1 + tva / 100);
      },

      handleOptionChange(option) {
        this.selected = option;
        this.applyFilter();
      },

      formatPrice(value) {
        const num = Number(value) || 0;
        return num.toFixed(2);
      },

      async saveConfigurations() {
        this.isSaving = true;
        this.errorMessage = "";

        try {
          const configurations = [];

          this.productGroups.forEach(group => {
            group.lines.forEach(line => {
              if (line.ttc > 0) {
                configurations.push({
                  variantId: line.variantId,
                  configuredPriceTtc: line.ttc.toFixed(2),
                });
              }
            });
          });

          if (configurations.length === 0) {
            this.errorMessage = "Aucune configuration à sauvegarder.";
            return;
          }

          await updateRationConfigurations(configurations);

          await this.loadConfigurationInterface();

          this.showSaveSuccessModal();
        } catch (error) {
          this.errorMessage = "Erreur lors de la sauvegarde. Veuillez réessayer.";
          console.error("Erreur sauvegarde:", error);
        } finally {
          this.isSaving = false;
        }
      },

      showResetConfirmation() {
        const modalElement = document.getElementById("resetPricesModal");
        if (modalElement) {
          try {
            if (window.bootstrap?.Modal) {
              const modal = new window.bootstrap.Modal(modalElement);
              modal.show();
            } else if (window.$ && window.$(modalElement).modal) {
              window.$(modalElement).modal("show");
            } else {
              modalElement.style.display = "block";
              modalElement.classList.add("show");
              document.body.classList.add("modal-open");
            }
          } catch (e) {
            console.warn("Erreur ouverture modal:", e);
            modalElement.style.display = "block";
            modalElement.classList.add("show");
            document.body.classList.add("modal-open");
          }
        }
      },

      closeResetModal() {
        const modalElement = document.getElementById("resetPricesModal");
        if (modalElement) {
          try {
            if (window.bootstrap?.Modal) {
              const modal = window.bootstrap.Modal.getInstance(modalElement);
              if (modal) {
                modal.hide();
              }
            } else if (window.$ && window.$(modalElement).modal) {
              window.$(modalElement).modal("hide");
            } else {
              modalElement.style.display = "none";
              modalElement.classList.remove("show");
              document.body.classList.remove("modal-open");
            }
          } catch (e) {
            console.warn("Erreur fermeture modal:", e);
            modalElement.style.display = "none";
            modalElement.classList.remove("show");
            document.body.classList.remove("modal-open");
          }
        }
      },

      showSaveSuccessModal() {
        const modalElement = document.getElementById("saveSuccessModal");
        if (modalElement) {
          try {
            if (window.bootstrap?.Modal) {
              const modal = new window.bootstrap.Modal(modalElement);
              modal.show();
            } else if (window.$ && window.$(modalElement).modal) {
              window.$(modalElement).modal("show");
            } else {
              modalElement.style.display = "block";
              modalElement.classList.add("show");
              document.body.classList.add("modal-open");
            }
          } catch (e) {
            console.warn("Erreur ouverture modal succès sauvegarde:", e);
            modalElement.style.display = "block";
            modalElement.classList.add("show");
            document.body.classList.add("modal-open");
          }
        }
      },

      closeSaveSuccessModal() {
        const modalElement = document.getElementById("saveSuccessModal");
        if (modalElement) {
          try {
            if (window.bootstrap?.Modal) {
              const modal = window.bootstrap.Modal.getInstance(modalElement);
              if (modal) {
                modal.hide();
              }
            } else if (window.$ && window.$(modalElement).modal) {
              window.$(modalElement).modal("hide");
            } else {
              modalElement.style.display = "none";
              modalElement.classList.remove("show");
              document.body.classList.remove("modal-open");
            }
          } catch (e) {
            console.warn("Erreur fermeture modal succès sauvegarde:", e);
            modalElement.style.display = "none";
            modalElement.classList.remove("show");
            document.body.classList.remove("modal-open");
          }
        }
      },

      showResetSuccessModal() {
        const modalElement = document.getElementById("resetSuccessModal");
        if (modalElement) {
          try {
            if (window.bootstrap?.Modal) {
              const modal = new window.bootstrap.Modal(modalElement);
              modal.show();
            } else if (window.$ && window.$(modalElement).modal) {
              window.$(modalElement).modal("show");
            } else {
              modalElement.style.display = "block";
              modalElement.classList.add("show");
              document.body.classList.add("modal-open");
            }
          } catch (e) {
            console.warn("Erreur ouverture modal succès réinitialisation:", e);
            modalElement.style.display = "block";
            modalElement.classList.add("show");
            document.body.classList.add("modal-open");
          }
        }
      },

      closeResetSuccessModal() {
        const modalElement = document.getElementById("resetSuccessModal");
        if (modalElement) {
          try {
            if (window.bootstrap?.Modal) {
              const modal = window.bootstrap.Modal.getInstance(modalElement);
              if (modal) {
                modal.hide();
              }
            } else if (window.$ && window.$(modalElement).modal) {
              window.$(modalElement).modal("hide");
            } else {
              modalElement.style.display = "none";
              modalElement.classList.remove("show");
              document.body.classList.remove("modal-open");
            }
          } catch (e) {
            console.warn("Erreur fermeture modal succès réinitialisation:", e);
            modalElement.style.display = "none";
            modalElement.classList.remove("show");
            document.body.classList.remove("modal-open");
          }
        }
      },

      async confirmResetPrices() {
        const modalElement = document.getElementById("resetPricesModal");
        if (modalElement) {
          try {
            if (window.bootstrap?.Modal) {
              const modal = window.bootstrap.Modal.getInstance(modalElement);
              if (modal) {
                modal.hide();
              }
            } else if (window.$ && window.$(modalElement).modal) {
              window.$(modalElement).modal("hide");
            } else {
              modalElement.style.display = "none";
              modalElement.classList.remove("show");
              document.body.classList.remove("modal-open");
            }
          } catch (e) {
            console.warn("Erreur fermeture modal:", e);
            modalElement.style.display = "none";
            modalElement.classList.remove("show");
            document.body.classList.remove("modal-open");
          }
        }

        await this.resetPricesToDefault();
      },

      async resetPricesToDefault() {
        this.isResetting = true;
        this.errorMessage = "";

        try {
          const configurations = [];
          this.originalProductsData.forEach(product => {
            product.variants.forEach(variant => {
              const newPriceTtc = variant.actualPrice * (1 + variant.taxe / 100);
              configurations.push({
                variantId: variant.variantId,
                configuredPriceTtc: newPriceTtc.toFixed(2),
              });
            });
          });

          if (configurations.length === 0) {
            this.errorMessage = "Aucune configuration à réinitialiser.";
            return;
          }
          await updateRationConfigurations(configurations);

          await this.loadConfigurationInterface();
          this.showResetSuccessModal();
        } catch (error) {
          this.errorMessage = "Erreur lors de la réinitialisation des prix. Veuillez réessayer.";
          console.error("Erreur réinitialisation prix:", error);
        } finally {
          this.isResetting = false;
        }
      },

      cancelConfiguration() {
        this.mode = "params-home";
        this.productGroups = [];
        this.originalProductsData = [];
        this.errorMessage = "";
      },
    },
  };
</script>
